import { useEffect, useMemo, useRef, useState } from 'react';
import { arcadeAudio } from '../../platform/audio';
import type { GameProps } from '../../platform/types';
import './nest.css';

type Dir = 'NE' | 'NW' | 'SE' | 'SW';
const DIRS: Dir[] = ['SE', 'SW', 'NW', 'NE'];

interface Piece {
  id: string;
  kind: string;
  x: number;
  y: number;
  tx: number;
  ty: number;
  dir: Dir;
  tdir: Dir;
  placed: boolean;
}

const ROOMS: Array<{ name: string; w: number; h: number; items: Array<[string, number, number, Dir]> }> = [
  { name: 'Studio 1A', w: 720, h: 460, items: [['bedSingle', 120, 80, 'SE'], ['desk', 420, 90, 'SW'], ['chairDesk', 500, 160, 'NW'], ['plantSmall1', 80, 300, 'SE']] },
  { name: 'Loft 2B', w: 760, h: 500, items: [['loungeSofa', 160, 220, 'SE'], ['pillow', 300, 280, 'SE'], ['lampRoundFloor', 80, 180, 'SE'], ['bookcaseOpen', 560, 80, 'SW'], ['rugRectangle', 220, 300, 'SE']] },
  { name: 'Kitchenette', w: 740, h: 480, items: [['kitchenFridge', 80, 80, 'SE'], ['kitchenStove', 240, 90, 'SE'], ['kitchenSink', 400, 90, 'SE'], ['kitchenCabinet', 560, 90, 'SE'], ['chair', 300, 300, 'NE']] },
  { name: 'Bath', w: 640, h: 440, items: [['bathtub', 80, 80, 'SE'], ['bathroomSink', 360, 80, 'SE'], ['plantSmall1', 500, 260, 'SW']] },
  { name: 'Office', w: 760, h: 500, items: [['desk', 180, 140, 'SE'], ['chairDesk', 260, 210, 'NW'], ['computerScreen', 210, 90, 'SE'], ['bookcaseClosed', 520, 80, 'SW'], ['lampRoundTable', 400, 160, 'SE']] },
  { name: 'Guest', w: 720, h: 480, items: [['bedDouble', 100, 80, 'SE'], ['lampRoundTable', 320, 90, 'SE'], ['pillow', 200, 160, 'SE'], ['loungeChair', 480, 240, 'NW']] },
  { name: 'Chaos Hall', w: 800, h: 520, items: [['cardboardBoxClosed', 80, 80, 'SE'], ['cardboardBoxOpen', 200, 200, 'SW'], ['chair', 400, 80, 'NE'], ['chairCushion', 520, 260, 'SE'], ['pottedPlant', 680, 80, 'SE'], ['bear', 360, 340, 'SW']] },
  { name: 'Family', w: 820, h: 540, items: [['loungeSofa', 80, 260, 'SE'], ['loungeChair', 360, 200, 'NE'], ['pillow', 220, 320, 'SE'], ['computerScreen', 200, 80, 'SE'], ['cabinetTelevision', 200, 140, 'SE'], ['rugRectangle', 140, 360, 'SE']] },
  { name: 'Laundry', w: 680, h: 440, items: [['dryer', 80, 120, 'SE'], ['kitchenCabinet', 240, 120, 'SE'], ['cardboardBoxClosed', 420, 220, 'SW']] },
  { name: 'Penthouse', w: 860, h: 560, items: [['bedDouble', 80, 80, 'SE'], ['loungeSofa', 420, 280, 'NE'], ['bookcaseOpen', 700, 80, 'SW'], ['plantSmall1', 40, 320, 'SE'], ['lampRoundFloor', 360, 80, 'SE'], ['pillow', 500, 360, 'SE']] },
  { name: 'Moving Van', w: 780, h: 500, items: [['cardboardBoxClosed', 80, 80, 'SE'], ['cardboardBoxClosed', 200, 80, 'SE'], ['cardboardBoxOpen', 320, 80, 'SE'], ['chair', 80, 240, 'NE'], ['desk', 280, 240, 'SW'], ['plantSmall1', 520, 240, 'SE'], ['bear', 640, 320, 'SW']] },
  { name: 'Finale Suite', w: 880, h: 580, items: [['bedDouble', 60, 60, 'SE'], ['desk', 360, 80, 'SW'], ['chairDesk', 430, 150, 'NW'], ['loungeSofa', 560, 300, 'NE'], ['bookcaseClosed', 760, 60, 'SW'], ['kitchenFridge', 60, 320, 'SE'], ['pottedPlant', 260, 360, 'SE'], ['lampRoundFloor', 500, 80, 'SE']] },
];

function src(kind: string, dir: Dir) {
  return `/assets/furn/${kind}_${dir}.png`;
}

export function NestGame({ onExit, onResult }: GameProps) {
  const [room, setRoom] = useState(0);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [drag, setDrag] = useState<string | null>(null);
  const [stars, setStars] = useState(0);
  const [combo, setCombo] = useState(0);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const stage = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void arcadeAudio.playMusic('nest', '/assets/music/Farm-Frolics.ogg');
    return () => arcadeAudio.stopMusic();
  }, []);

  const spec = ROOMS[room];
  useEffect(() => {
    const cluttered = spec.items.map(([kind, tx, ty, tdir], i) => ({
      id: `${kind}-${i}`,
      kind,
      tx, ty, tdir,
      x: 40 + Math.random() * (spec.w - 160),
      y: 40 + Math.random() * (spec.h - 160),
      dir: DIRS[(Math.random() * 4) | 0],
      placed: false,
    }));
    setPieces(cluttered);
    setCombo(0);
  }, [room, spec]);

  const snap = (p: Piece): Piece => {
    const dx = p.x - p.tx;
    const dy = p.y - p.ty;
    const close = dx * dx + dy * dy < 36 * 36 && p.dir === p.tdir;
    if (close && !p.placed) {
      arcadeAudio.blip(640 + combo * 40, 0.07, 'triangle', 0.08);
      setCombo((c) => c + 1);
      setScore((s) => s + 120 + combo * 30);
      return { ...p, x: p.tx, y: p.ty, placed: true };
    }
    return p;
  };

  const remaining = pieces.filter((p) => !p.placed).length;
  useEffect(() => {
    if (pieces.length && remaining === 0) {
      setStars((s) => s + 3);
      if (room >= ROOMS.length - 1) {
        setDone(true);
        onResult({
          score,
          won: true,
          stamps: { clear: true, skill: score >= 6000, mastery: score >= 9000 },
        });
      } else {
        setTimeout(() => setRoom((r) => r + 1), 600);
      }
    }
  }, [remaining, pieces.length, room, score, onResult]);

  const onMove = (e: React.PointerEvent) => {
    if (!drag || !stage.current) return;
    const r = stage.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * spec.w - 40;
    const y = ((e.clientY - r.top) / r.height) * spec.h - 40;
    setPieces((ps) => ps.map((p) => (p.id === drag && !p.placed ? { ...p, x, y } : p)));
  };

  const rotate = (id: string) => {
    setPieces((ps) => ps.map((p) => {
      if (p.id !== id || p.placed) return p;
      const dir = DIRS[(DIRS.indexOf(p.dir) + 1) % 4];
      return snap({ ...p, dir });
    }));
  };

  const ghosts = useMemo(() => spec.items, [spec]);

  return (
    <div className="nest">
      <header className="nest-bar">
        <div>
          <p className="eyebrow">Room {room + 1} / {ROOMS.length}</p>
          <strong>{spec.name}</strong>
        </div>
        <span>{score} · combo {combo} · {remaining} left</span>
      </header>
      <div
        className="nest-stage"
        ref={stage}
        style={{ aspectRatio: `${spec.w} / ${spec.h}` }}
        onPointerMove={onMove}
        onPointerUp={() => {
          if (drag) setPieces((ps) => ps.map((p) => (p.id === drag ? snap(p) : p)));
          setDrag(null);
        }}
      >
        {ghosts.map(([kind, x, y, dir], i) => (
          <img key={`g${i}`} className="ghost" src={src(kind, dir)} style={{ left: x, top: y }} alt="" />
        ))}
        {pieces.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`furn ${p.placed ? 'in' : ''} ${drag === p.id ? 'drag' : ''}`}
            style={{ left: p.x, top: p.y }}
            onPointerDown={(e) => {
              if (p.placed) return;
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              setDrag(p.id);
            }}
            onDoubleClick={() => rotate(p.id)}
            onContextMenu={(e) => { e.preventDefault(); rotate(p.id); }}
          >
            <img src={src(p.kind, p.dir)} alt={p.kind} draggable={false} />
          </button>
        ))}
      </div>
      <p className="hint">Drag onto the pale ghost. Right-click or double-tap to rotate.</p>
      {done && (
        <div className="overlay">
          <div className="panel">
            <h2>The truck is closed</h2>
            <p>Score {score.toLocaleString()} · {stars} stars</p>
            <button type="button" className="play" onClick={onExit}>Lobby</button>
          </div>
        </div>
      )}
    </div>
  );
}
